/**
 * Derivacao de chaves — tudo com BLAKE2b (`crypto_generichash`), usado no
 * modo com chave, que e um MAC seguro e serve de KDF.
 *
 * Cada uso tem um rotulo de dominio proprio. Rotulos diferentes = chaves
 * independentes, mesmo partindo do mesmo segredo.
 */
import { ascii, concat } from './bytes.js'
import { SIZES, sodium } from './sodium.js'

export const DOMAIN = {
  x3dh: ascii('CriptoHomer/X3DH/v1'),
  rootRatchet: ascii('CriptoHomer/RootKDF/v1'),
  chainStep: ascii('CriptoHomer/ChainKDF/v1'),
  messageKey: ascii('CriptoHomer/MessageKey/v1'),
  rootExpandRoot: ascii('CriptoHomer/RootKDF/root/v1'),
  rootExpandChain: ascii('CriptoHomer/RootKDF/chain/v1'),
  rootExpandHeader: ascii('CriptoHomer/RootKDF/header/v1'),
  headerKeyInitiator: ascii('CriptoHomer/HeaderKey/Initiator/v1'),
  headerKeyResponder: ascii('CriptoHomer/HeaderKey/Responder/v1'),
  safetyCode: ascii('CriptoHomer/SafetyCode/v1'),
  otpMac: ascii('CriptoHomer/OtpMac/v1'),
} as const

/** Maior saida que o BLAKE2b produz numa chamada. */
export const MAX_KDF_OUTPUT_BYTES = 64

/** BLAKE2b com chave. Nunca peca mais de 64 bytes de uma vez. */
export function kdf(key: Uint8Array, info: Uint8Array, outLength: number): Uint8Array {
  if (outLength < 1 || outLength > MAX_KDF_OUTPUT_BYTES) {
    throw new RangeError(
      `BLAKE2b produz de 1 a ${MAX_KDF_OUTPUT_BYTES} bytes, pedido: ${outLength}`,
    )
  }
  return sodium().crypto_generichash(outLength, info, key)
}

/**
 * Passo do root ratchet: extrai um segredo do DH e expande em tres chaves.
 *
 * Extract-then-expand no estilo do HKDF, e nao um unico corte de 96 bytes,
 * porque o BLAKE2b nao emite mais de 64 bytes por chamada. Cada saida tem
 * rotulo proprio, entao conhecer uma nao ajuda a descobrir as outras.
 */
export function kdfRootKey(
  rootKey: Uint8Array,
  dhOutput: Uint8Array,
): { rootKey: Uint8Array; chainKey: Uint8Array; nextHeaderKey: Uint8Array } {
  const extracted = kdf(rootKey, concat(DOMAIN.rootRatchet, dhOutput), SIZES.symmetricKey)
  return {
    rootKey: kdf(extracted, DOMAIN.rootExpandRoot, SIZES.symmetricKey),
    chainKey: kdf(extracted, DOMAIN.rootExpandChain, SIZES.symmetricKey),
    nextHeaderKey: kdf(extracted, DOMAIN.rootExpandHeader, SIZES.symmetricKey),
  }
}

/**
 * Passo da chain de simetrica: avanca a chain key e produz a chave da
 * mensagem. Constantes distintas garantem que conhecer a chave da mensagem
 * nao revela a proxima chain key — e o que da sigilo futuro.
 */
export function kdfChainKey(chainKey: Uint8Array): {
  chainKey: Uint8Array
  messageKey: Uint8Array
} {
  return {
    chainKey: kdf(chainKey, DOMAIN.chainStep, SIZES.symmetricKey),
    messageKey: kdf(chainKey, DOMAIN.messageKey, SIZES.symmetricKey),
  }
}
